import { cloneDeep } from "lodash"

import { useEffect, useMemo, useRef, useState } from "react"
import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import { useTranslation } from "react-i18next"
import { Popover, MenuItem } from "@mui/material"
import Chip from "@/components/chip/Chip"
import { useSearchParams } from "@/hooks/useSearchParams"

import Selector from "@/components/form/selector/Selector"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"

import "./tacticMapFilter.scss"
import "@/app/report/components/filter/reportFilter.scss"
import Box from "@mui/system/Box/Box"
import { DEFAULT_ACTION_TYPES, DEFAULT_MAP_TYPES } from "@/app/report/common/tacticMapType.ts"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { ActivityDetail, ActivityParticipatedPlayerType } from "@/types/activity.ts"
import SessionSelector from "@/app/report/components/filter/sessionSelector/SessionSelector.tsx"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector.tsx"
import BasicButton from "@/components/button/BasicButton.tsx"
import Icon from "@/components/icon/Icon.tsx"
import { isEqualUnordered } from "@/app/report/common/tools.ts"

export default function TacticMapFilter({
  open,
  setOpen,
  selectedMapType,
  setSelectedMapType,
  sessionList,
  selectedSessionList,
  setSelectedSessionList,
  playerList,
  selectedPlayerList,
  setSelectedPlayerList,
  selectedActionType,
  setSelectedActionType,
  activityDetail,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  selectedMapType: string
  setSelectedMapType: (mapType: string) => void
  sessionList: number[]
  selectedSessionList: number[]
  setSelectedSessionList: (selectedSessionList: number[]) => void
  playerList: number[]
  selectedPlayerList: number[]
  setSelectedPlayerList: (selectedPlayerList: number[]) => void
  selectedActionType: string
  setSelectedActionType: (type: string) => void
  activityDetail: ActivityDetail
}) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()

  const searchParams = useSearchParams()
  const dailyReportType = searchParams.get("dailyReportType") || ACTIVITY_SESSION_TYPE.SESSION

  const anchorRef = useRef<HTMLButtonElement>(null)
  const [popoverVerticalOrigin, setPopoverVerticalOrigin] = useState<"top" | "bottom">("bottom")
  const [popoverMaxHeight, setPopoverMaxHeight] = useState<string>("calc(100vh - 100px)")

  const [tempSelectedSessionList, setTempSelectedSessionList] = useState<number[]>([])
  const [tempSelectedPlayerList, setTempSelectedPlayerList] = useState<
    ActivityParticipatedPlayerType[]
  >([])

  const [tempSelectedMapType, setTempSelectedMapType] = useState<string>(selectedMapType)
  const [tempSelectedActionType, setTempSelectedActionType] = useState<string>(selectedActionType)

  const [sessionSelectorOpen, setSessionSelectorOpen] = useState(false)
  const [playerSelectorOpen, setPlayerSelectorOpen] = useState(false)
  const [mapTypeSelectorOpen, setMapTypeSelectorOpen] = useState(false)
  const [actionTypeSelectorOpen, setActionTypeSelectorOpen] = useState(false)

  const [selectedType, setSelectedType] = useState<ACTIVITY_SESSION_TYPE>(
    dailyReportType === ACTIVITY_SESSION_TYPE.SESSION
      ? ACTIVITY_SESSION_TYPE.SESSION
      : ACTIVITY_SESSION_TYPE.ACTIVITY,
  )

  useEffect(() => {
    if (open && activityDetail?.sessions?.length && selectedSessionList?.length) {
      const filteredSessions = activityDetail.sessions
        .filter((s) => selectedSessionList.includes(s.idfSession))
        .map((s) => s.idfSession)

      setTempSelectedSessionList(filteredSessions)
    }
  }, [open, activityDetail, selectedSessionList])

  useEffect(() => {
    if (open && activityDetail?.players?.length && selectedPlayerList?.length) {
      const accountInfo = createAccountStore.getState().info
      const isPlayerAccount = accountInfo?.type === AccountTypeKey.Player
      const playerIdfAccount = accountInfo?.idfAccount

      let filteredPlayers = activityDetail.players.filter((p) =>
        selectedPlayerList.includes(p.idfPlayer),
      )

      if (isPlayerAccount && playerIdfAccount) {
        filteredPlayers = filteredPlayers.filter((p) => p.idfPlayer === playerIdfAccount)
      }

      setTempSelectedPlayerList(filteredPlayers)
    }
  }, [open, activityDetail, selectedPlayerList])

  useEffect(() => {
    setSelectedType(
      dailyReportType === ACTIVITY_SESSION_TYPE.SESSION
        ? ACTIVITY_SESSION_TYPE.SESSION
        : ACTIVITY_SESSION_TYPE.ACTIVITY,
    )
  }, [dailyReportType])

  const tempActivityDetail = useMemo(() => {
    const accountInfo = createAccountStore.getState().info
    const isPlayerAccount = accountInfo?.type === AccountTypeKey.Player
    const playerIdfAccount = accountInfo?.idfAccount

    let filteredPlayers = activityDetail.players.filter((p) => playerList.includes(p.idfPlayer))

    if (isPlayerAccount && playerIdfAccount) {
      filteredPlayers = filteredPlayers.filter((p) => p.idfPlayer === playerIdfAccount)
    }

    return {
      ...cloneDeep(activityDetail),
      sessions:
        dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY
          ? []
          : activityDetail.sessions.filter((s) => {
              return sessionList.includes(s.idfSession)
            }),
      players: filteredPlayers,
    }
  }, [activityDetail, dailyReportType, sessionList, playerList])

  const applyDisabled = useMemo(() => {
    const currentType =
      dailyReportType === ACTIVITY_SESSION_TYPE.SESSION
        ? ACTIVITY_SESSION_TYPE.SESSION
        : ACTIVITY_SESSION_TYPE.ACTIVITY

    return (
      tempSelectedMapType === selectedMapType &&
      tempSelectedActionType === selectedActionType &&
      selectedType === currentType &&
      isEqualUnordered(tempSelectedSessionList, selectedSessionList) &&
      isEqualUnordered(
        tempSelectedPlayerList.map((tsp) => tsp.idfPlayer),
        selectedPlayerList,
      )
    )
  }, [
    tempSelectedMapType,
    tempSelectedSessionList,
    tempSelectedPlayerList,
    tempSelectedActionType,
    selectedType,
    dailyReportType,
    selectedMapType,
    selectedActionType,
    selectedSessionList,
    selectedPlayerList,
  ])

  const handleApply = () => {
    setSelectedMapType(tempSelectedMapType)
    setSelectedSessionList(tempSelectedSessionList)
    setSelectedPlayerList(tempSelectedPlayerList.map((tsp) => tsp.idfPlayer))
    setSelectedActionType(tempSelectedActionType)
    setOpen(false)
  }

  const handleReset = () => {}

  return (
    <div className="tactic-map-filter">
      <BasicButton
        type="default-outlined"
        size="sm"
        icon="filter"
        ref={anchorRef}
        onClick={() => {
          if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            const spaceAbove = rect.top
            const shouldOpenUpward = spaceBelow < 500 && spaceAbove > spaceBelow
            setPopoverVerticalOrigin(shouldOpenUpward ? "top" : "bottom")
            setPopoverMaxHeight(
              shouldOpenUpward
                ? `${Math.min(rect.top - 20, 600)}px`
                : `${Math.min(spaceBelow - 20, 600)}px`,
            )
          }
          setOpen(!open)
        }}>
        {t("filter")}
      </BasicButton>
      <Popover
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        disableScrollLock
        anchorOrigin={{
          vertical: popoverVerticalOrigin,
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: popoverVerticalOrigin === "bottom" ? "top" : "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: popoverMaxHeight,
              overflowY: "auto",
            },
          },
        }}
        sx={{
          mt: popoverVerticalOrigin === "bottom" ? 1 : 0,
          mb: popoverVerticalOrigin === "top" ? 1 : 0,
          zIndex: 8,
        }}>
        <div className="tactic-map-filter-popover">
          <Box className="tactic-map-filter-menu">
            <Box className="tactic-map-filter-menu-box report-filter-selector">
              <Selector
                type="select"
                open={mapTypeSelectorOpen}
                onOpen={() => setMapTypeSelectorOpen(true)}
                onClose={() => setMapTypeSelectorOpen(false)}
                value={tempSelectedMapType}
                onChange={() => {}}
                renderValue={() => (
                  <span className="report-filter-selector-value">
                    {t("mapType")}
                    <Chip
                      type="default-small"
                      label={t(
                        DEFAULT_MAP_TYPES.find((item) => item.value === tempSelectedMapType)
                          ?.label || "",
                      )}
                    />
                  </span>
                )}
                placeholder={t("select")}>
                {DEFAULT_MAP_TYPES.map((mapType) => (
                  <MenuItem
                    key={mapType.value}
                    value={mapType.value}
                    onClick={() => {
                      setTempSelectedMapType(mapType.value)
                    }}>
                    <p className="tactic-map-filter-menu-select-menu">{t(mapType.label)}</p>
                    {mapType.value === tempSelectedMapType ? (
                      <Icon type="check" className="tactic-map-type-check-icon" />
                    ) : (
                      ""
                    )}
                  </MenuItem>
                ))}
              </Selector>
            </Box>
          </Box>
          <Box className="tactic-map-filter-menu">
            <Box className="tactic-map-filter-menu-box tactic-map-session-filter">
              <SessionSelector
                open={sessionSelectorOpen}
                setOpen={setSessionSelectorOpen}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                selectedSessionList={tempSelectedSessionList}
                setSelectedSessionList={setTempSelectedSessionList}
                detail={tempActivityDetail}
                hideRadioSelection={dailyReportType === ACTIVITY_SESSION_TYPE.SESSION}
                disabled={dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY}
              />
            </Box>
          </Box>
          <Box className="tactic-map-filter-menu">
            <Box className="tactic-map-filter-menu-box tactic-map-player-filter">
              <PlayerSelector
                open={playerSelectorOpen}
                setOpen={setPlayerSelectorOpen}
                selectedActivity={[tempActivityDetail.idfActivity]}
                players={tempActivityDetail?.players || []}
                selectedPlayerList={tempSelectedPlayerList}
                setSelectedPlayerList={setTempSelectedPlayerList}
                paramsVersion={false}
              />
            </Box>
          </Box>
          <Box className="tactic-map-filter-menu">
            <Box className="tactic-map-filter-menu-box report-filter-selector">
              <Selector
                type="select"
                open={actionTypeSelectorOpen}
                onOpen={() => setActionTypeSelectorOpen(true)}
                onClose={() => setActionTypeSelectorOpen(false)}
                value={tempSelectedActionType}
                onChange={() => {}}
                renderValue={() => (
                  <span className="report-filter-selector-value">
                    {t("actionType")}
                    <Chip
                      type="default-small"
                      label={translateKey(
                        DEFAULT_ACTION_TYPES.find((item) => item.value === tempSelectedActionType)
                          ?.label || "",
                      )}
                    />
                  </span>
                )}
                placeholder={t("select")}>
                {DEFAULT_ACTION_TYPES.map((action) => (
                  <MenuItem
                    key={action.value}
                    value={action.value}
                    onClick={() => {
                      setTempSelectedActionType(action.value)
                    }}>
                    <p className="tactic-map-filter-menu-select-menu">
                      {translateKey(action.label)}
                    </p>
                    {action.value === tempSelectedActionType ? (
                      <Icon type="check" className="tactic-map-type-check-icon" />
                    ) : (
                      ""
                    )}
                  </MenuItem>
                ))}
              </Selector>
            </Box>
          </Box>
          <Box className="tactic-map-filter-reset-apply-button">
            <Box className="tactic-map-filter-reset-apply-button-box">
              <div className="tactic-map-reset-button">
                <BasicButton
                  type="primary-contained"
                  size="sm"
                  disabled={applyDisabled}
                  onClick={handleReset}>
                  <img src="/images/ri-reset-left-line.png" width={20} height={20} />
                  <div className="tactic-map-reset-button-text">{t("button.reset")}</div>
                </BasicButton>
              </div>
              <div className="tactic-map-apply-button">
                <BasicButton
                  type="primary-contained"
                  disabled={applyDisabled}
                  onClick={handleApply}>
                  <div className="tactic-map-apply-button-text">{t("button.apply")}</div>
                </BasicButton>
              </div>
            </Box>
          </Box>
        </div>
      </Popover>
    </div>
  )
}
